/**
 * Copied from EQX-shared quota-bar component
 */

import EqxProgressBar from '../EqxProgressBar/EqxProgressBar'
import './EqxQuotaBar.css'

interface EqxQuotaBarProps {
    quota: number // The quota for the resource (holiday allowance, credit limit)
    items: EqxQuotaBarItem[] //Array of objects for each bar to show in the stack
    labels: Partial<EqxQuotaBarLabels> //List of different labels to be used on the chart, keys are unused|used|quota|items. If the value is set to a string this is assumed to be label, all must be present, if omitted defaults will be used
    format?: (amount: number) => string //Accepts amount argument, returns formatted string (eg if we're dealing with currency, should return amounts with currency symbol)
    autoColour?: (amount: number, item: EqxQuotaBarItem) => string //the function to use to decide the colour, accepts amount and item argument. Defaults to a function which checks warningThreshold and dangerThreshold parameters to decide which colour class to show.
    warningThreshold?: number //defaults to 0.8 - the threshold (percentage of quota) that default autoColour function uses to show warning colour. Ignored if divideOverThresholdItem is true
    dangerThreshold?: number //defaults to 1 - the threshold (percentage of quota) that default autoColour function uses to show danger colour
    useItemsForColours?: boolean //defaults to false - whether to pass the current cumulative total to colour functions or the overall total. If true it likely shows leftmost items in green and rightmost items over the quota in red. If set to true warningThreshold is ignored and not shown by default colour function. If false by default all items are the same colour, based on whether total is over or under limit.
    divideOverThresholdItem?: boolean // defaults to false - if useItemsForColours is true, then the item that takes us over the quota is divided into two, the part under the quota and the part over it.
}
interface HasLabel {
    label: string
}
interface EqxQuotaBarItem extends HasLabel {
    label: string //Label for the bar
    amount: number //The amount, using the same units as quota
    ghosted?: boolean //Whether to show the bar as semi-transparent
    colour?: string | ((value: number, itemOrLabel: HasLabel) => string) //Bootstrap class name bg-* to give it a colour, e.g. success, danger. If a function is provided then a figure will be passed in so it can decide what colour to show. If omitted it will use the autoColour option
    formatLabel?: string | ((value: number, itemOrLabel: HasLabel) => string) //Text to show on the bar. If a function is passed, amount and label will be passed as parameter, Defaults to a function which returns amount (formatted) + item.label
}
interface EqxQuotaBarLabels {
    used: EqxQuotaBarLabel //Label for the used amount
    unused: EqxQuotaBarLabel //Label for the unused amount
    quota: EqxQuotaBarLabel //Label for the quota amount
    items: EqxQuotaBarLabel //Label for the items amount
}
interface EqxQuotaBarLabel extends HasLabel {
    label: string //The label text - defaults to unused = "Unused", used = "Used", quota = "Limit"
    show: boolean //Whether to hide or show the label (defaults to true)
    formatLabel?: (value: string, itemOrLabel: HasLabel) => string //See formatLabel for item
}
//parse labels
const parseLabels = (labels: Partial<EqxQuotaBarLabels>) => {
    const defaultFormatLabel = (value: string, itemOrLabel: HasLabel) => value + ' ' + itemOrLabel.label
    //parse labels
    if (!labels.used) labels.used = { label: 'Used', show: true, formatLabel: defaultFormatLabel }
    if (!labels.unused) labels.unused = { label: 'Unused', show: true, formatLabel: defaultFormatLabel }
    if (!labels.quota) labels.quota = { label: 'Limit', show: true, formatLabel: defaultFormatLabel }
    if (!labels.items) labels.items = { label: '', show: true, formatLabel: defaultFormatLabel }
    //loop through and fix any missing values
    Object.entries(labels).forEach(function ([key, label]) {
        if (!label.show) label.show = true
        if (!label.formatLabel) {
            label.formatLabel = defaultFormatLabel
        }
    })
    return {
        used: labels.used,
        unused: labels.unused,
        quota: labels.quota,
        items: labels.items,
    }
}
const EqxQuotaBar = (props: EqxQuotaBarProps) => {
    const quota = props.quota
    const items = props.items
    const labels = parseLabels(props.labels)
    const format = props.format ?? ((amount) => amount.toString())
    const autoColour = props.autoColour
    const warningThreshold = props.warningThreshold
    const dangerThreshold = props.dangerThreshold
    const useItemsForColours = props.useItemsForColours
    const divideOverThresholdItem = props.divideOverThresholdItem

    //items with split
    const splitItems = () => {
        //loop through items
        let total = 0
        let subitems = []
        for (var i in items) {
            const item = items[i]
            if (divideOverThresholdItem && useItemsForColours && total + item.amount > quota && total <= quota) {
                const belowAmount = quota - total
                const aboveAmount = item.amount - belowAmount

                const belowItem = { ...item }
                belowItem.amount = belowAmount
                belowItem.label = item.label + ' (Within ' + quotaLabel + ')'

                const aboveItem = { ...item }
                aboveItem.amount = aboveAmount
                aboveItem.label = item.label + ' (Over ' + quotaLabel + ')'

                subitems.push(belowItem)
                subitems.push(aboveItem)
            } else {
                subitems.push(item)
            }
        }
        return subitems
    }

    //check quota > 0
    if (quota <= 0) {
        console.error('Quota must be greater than 0')
        return <></>
    }

    //get total amount of items in the array
    const getItemTotalAmount = () => {
        return items.reduce((acc, item) => acc + item.amount, 0)
    }
    //get percentage of quota
    const getPercentageOfQuota = (amount: number) => {
        return amount / quota
    }
    //is over quota
    const isOverQuota = () => {
        return getItemTotalAmount() > quota
    }
    //get percentage of quota
    const getPercentageOnChart = (percentage: number) => {
        if (isOverQuota()) {
            const totalPc = getItemTotalAmount() / quota
            return percentage / totalPc
        } else {
            return percentage
        }
    }

    //total amount
    const total = getItemTotalAmount()

    //get params
    const used = getItemTotalAmount()
    const usedFormatted = format(used)
    const usedLabel = labels.used.label
    const usedPercentage = 100 * getPercentageOnChart(getPercentageOfQuota(used))
    const usedLabelShowLeft = usedPercentage > 50

    const quotaFormatted = format(quota)
    const quotaLabel = labels.quota.label
    const quotaPercentage = 100 * getPercentageOnChart(1)

    const unused = Math.max(0, quota - total)
    const unusedFormatted = format(unused)
    const unusedLabel = labels.unused.label

    //get split items
    const itemsSplit = splitItems()

    return (
        <>
            <div className="eqx-quota-bar">
                {labels.used.show && (
                    <div className="progress progress-labels mb-0">
                        <div className="progress-bar" style={{ width: usedPercentage + '%' }}>
                            <div className="progress-bar-label progress-bar-label-left">{format(0)}</div>
                            {usedLabelShowLeft && (
                                <div
                                    data-toggle="tooltip"
                                    data-bs-toggle="tooltip"
                                    title={usedLabel}
                                    className="progress-bar-label progress-bar-label-right"
                                >
                                    {labels.used.formatLabel!(usedFormatted, labels.used)}
                                </div>
                            )}
                        </div>
                        <div className="progress-bar" role="progressbar" style={{ width: 100 - usedPercentage + '%' }}>
                            {!usedLabelShowLeft && (
                                <div
                                    data-toggle="tooltip"
                                    data-bs-toggle="tooltip"
                                    title={usedLabel}
                                    className="progress-bar-label progress-bar-label-left"
                                >
                                    {labels.used.formatLabel!(usedFormatted, labels.used)}
                                </div>
                            )}
                        </div>
                    </div>
                )}
                <EqxProgressBar
                    items={itemsSplit
                        .map((item, index) => {
                            const amount = item.amount
                            const amountFormatted = format(amount)
                            const colour = item.colour
                                ? typeof item.colour === 'string'
                                    ? item.colour
                                    : item.colour(item.amount, item)
                                : autoColour
                                    ? autoColour(item.amount, item)
                                    : 'primary'

                            const label = item.formatLabel
                                ? typeof item.formatLabel == 'function'
                                    ? item.formatLabel(item.amount, item)
                                    : item.formatLabel
                                : labels.items.formatLabel!(amountFormatted, item)

                            //calculate width
                            let percentage = 100 * getPercentageOnChart(getPercentageOfQuota(amount))
                            return (
                                <EqxProgressBar
                                    key={index}
                                    now={percentage}
                                    ghosted={item.ghosted}
                                    label={label}
                                    variant={colour}
                                />
                            )
                        })
                        .concat([
                            <EqxProgressBar
                                key={items.length}
                                variant="invisible"
                                label={labels.unused.formatLabel!(unusedFormatted, labels.unused)}
                                now={100 * getPercentageOnChart(getPercentageOfQuota(unused))}
                            />,
                        ])}
                />
                {labels.quota.show && (
                    <div className="progress progress-labels mt-0">
                        <div className="progress-bar" role="progressbar" style={{ width: quotaPercentage + '%' }}>
                            <div
                                data-toggle="tooltip"
                                data-bs-toggle="tooltip"
                                title={quotaLabel}
                                className="progress-bar-label progress-bar-label-left"
                            >
                                {format(0)}
                            </div>
                            <div
                                data-toggle="tooltip"
                                data-bs-toggle="tooltip"
                                title={quotaLabel}
                                className="progress-bar-label progress-bar-label-right"
                            >
                                {labels.quota.formatLabel!(quotaFormatted, labels.quota)}
                            </div>
                        </div>
                    </div>
                )}
            </div>
        </>
    )
}
export default EqxQuotaBar
